'use client'

import { ClipboardIcon, CodeBracketIcon, Cog6ToothIcon, DocumentPlusIcon, EyeIcon, FolderIcon } from '@heroicons/react/24/outline'
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from '@headlessui/react'
import { ProjectMetadata, projectService } from '@/services/ProjectService'
import { useEffect, useState } from 'react'

import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { SidebarTab } from '../../../types'
import { cn } from '@/lib/utils'
import { dbStorage } from '@/utils/dbStorage'
import { useAuth } from '@clerk/clerk-react'

// Define a type for the project and action items
interface ItemWithUrl {
  url: string;
  [key: string]: string | number | React.ComponentType<React.SVGProps<SVGSVGElement>> | undefined;
}

// Remove hardcoded projects array
// const projects = [
//   { id: 1, name: 'Workflow Inc. / Website Redesign', url: '#' },
//   // More projects...
// ]
// const recent = [projects[0]]

const quickActions = [
  { name: 'New Project...', icon: DocumentPlusIcon, shortcut: 'P', action: 'newProject' },
  { name: 'List Projects', icon: ClipboardIcon, shortcut: 'L', action: 'openProject' },
  { name: 'Open Preview', icon: EyeIcon, shortcut: 'O', action: 'openPreview' },
  { name: 'Edit Script', icon: CodeBracketIcon, shortcut: 'E', action: 'editScript' },
  { name: 'Edit Config', icon: Cog6ToothIcon, shortcut: 'C', action: 'editConfig' },
]

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNewProject?: () => void;
  onOpenProject?: () => void;
  onOpenPreview?: () => void;
  setSelectedFile?: (filePath: string) => void;
  activeTab?: SidebarTab;
  setActiveTab?: (tab: SidebarTab) => void;
}

export default function CommandPalette({
  isOpen,
  onClose,
  onNewProject,
  onOpenProject,
  onOpenPreview,
  setSelectedFile,
  activeTab,
  setActiveTab,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [recentProjects, setRecentProjects] = useState<ProjectMetadata[]>([])
  const [allProjects, setAllProjects] = useState<ProjectMetadata[]>([])
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)
  const { getToken, userId, orgId } = useAuth();

  // Fetch projects from IndexedDB
  useEffect(() => {
    const fetchProjectsFromCache = async () => {
      try {
        setIsLoadingProjects(true);
        // Get organization ID for cache key
        const organizationId = orgId || null;

        console.log('Fetching projects from IndexedDB for command palette');
        const projects = await dbStorage.getProjects(organizationId);

        // If cache is empty, request a refresh but keep loading state
        if (projects.length === 0) {
          console.log('Project cache is empty, requesting refresh');
          window.dispatchEvent(new CustomEvent('refreshProjects'));
          // Keep the loading state but don't update the projects arrays yet
          return;
        }

        setAllProjects(projects);
        // Get the 3 most recent projects (sorted by timestamp if available)
        const sortedProjects = [...projects].sort((a, b) => {
          const aTime = a.updatedAt || a.createdAt;
          const bTime = b.updatedAt || b.createdAt;
          return new Date(bTime).getTime() - new Date(aTime).getTime();
        });
        setRecentProjects(sortedProjects.slice(0, 3));
        setIsLoadingProjects(false);
      } catch (error) {
        console.error('Error fetching projects from cache:', error);
        // Set empty arrays to prevent further errors
        setAllProjects([]);
        setRecentProjects([]);
        setIsLoadingProjects(false);
      }
    };

    if (isOpen) {
      fetchProjectsFromCache();
    }
  }, [isOpen, orgId]);

  // Listen for project refresh events
  useEffect(() => {
    const handleProjectsRefreshed = () => {
      if (isOpen) {
        console.log('Projects refreshed, updating command palette');
        const refreshProjectsFromCache = async () => {
          try {
            const organizationId = orgId || null;
            const projects = await dbStorage.getProjects(organizationId);
            setAllProjects(projects);
            const sortedProjects = [...projects].sort((a, b) => {
              const aTime = a.updatedAt || a.createdAt;
              const bTime = b.updatedAt || b.createdAt;
              return new Date(bTime).getTime() - new Date(aTime).getTime();
            });
            setRecentProjects(sortedProjects.slice(0, 3));
            setIsLoadingProjects(false);
          } catch (error) {
            console.error('Error refreshing projects in command palette:', error);
            setIsLoadingProjects(false);
          }
        };
        refreshProjectsFromCache();
      }
    };

    window.addEventListener('projectsRefreshed', handleProjectsRefreshed);
    return () => {
      window.removeEventListener('projectsRefreshed', handleProjectsRefreshed);
    };
  }, [isOpen, orgId]);

  const filteredProjects =
    query === ''
      ? []
      : allProjects.filter((project) => {
        // Search across multiple project properties
        const searchTerm = query.toLowerCase();
        return (
          project.name.toLowerCase().includes(searchTerm) ||
          (project.id && project.id.toLowerCase().includes(searchTerm)) ||
          (project.userId && project.userId.toLowerCase().includes(searchTerm)) ||
          (project.organizationId && project.organizationId.toLowerCase().includes(searchTerm))
        );
      })

  // Handle keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Command palette toggle
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          onClose();
        }
        return;
      }

      // Quick action shortcuts
      if (isOpen && (e.metaKey || e.ctrlKey)) {
        const action = quickActions.find(a => a.shortcut.toLowerCase() === e.key.toLowerCase());
        if (action) {
          e.preventDefault();
          handleAction(action.action as string);
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle action execution
  const handleAction = (action: string) => {
    switch (action) {
      case 'newProject':
        if (onNewProject) onNewProject();
        break;
      case 'openProject':
        if (onOpenProject) onOpenProject();
        break;
      case 'openPreview':
        if (onOpenPreview) onOpenPreview();
        break;
      case 'editScript':
        if (new URLSearchParams(window.location.search).has('project')) {
          if (setActiveTab) {
            setActiveTab(SidebarTab.Editor);
          }
          if (setSelectedFile) {
            setSelectedFile('/project/hello.py');
          }
        }
        break;
      case 'editConfig':
        if (new URLSearchParams(window.location.search).has('project')) {
          if (setActiveTab) {
            setActiveTab(SidebarTab.Editor);
          }
          if (setSelectedFile) {
            setSelectedFile('/project/preswald.toml');
          }
        }
        break;
    }
    onClose();
  }

  return (
    <Dialog
      className="fixed inset-0 z-[100]"
      open={isOpen}
      onClose={() => {
        onClose()
        setQuery('')
      }}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
        <DialogPanel
          transition
          className="mx-auto max-w-2xl transform divide-y divide-gray-100 overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
        >
          <Combobox
            onChange={(item: ItemWithUrl | null) => {
              if (item) {
                if ('action' in item) {
                  handleAction(item.action as string);
                } else {
                  window.location.href = item.url;
                }
              }
            }}
          >
            <div className="relative">
              <ComboboxInput
                autoFocus
                className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                placeholder="Search projects or type a command..."
                onChange={(event) => setQuery(event.target.value)}
                onBlur={() => setQuery('')}
              />
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </div>

            {isLoadingProjects && (
              <div className="p-4 text-center">
                <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent text-gray-400 motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                <p className="mt-2 text-sm text-gray-500">Loading projects...</p>
              </div>
            )}

            {!isLoadingProjects && (query === '' || filteredProjects.length > 0) && (
              <ComboboxOptions static as="ul" className="max-h-[60vh] scroll-py-2 divide-y divide-gray-100 overflow-y-auto">
                <li className="p-2">
                  {query === '' && (
                    <h2 className="mb-2 mt-4 px-3 text-xs font-medium text-gray-500">Recent projects</h2>
                  )}
                  <ul className="text-sm text-gray-700">
                    {(query === '' ? recentProjects : filteredProjects).map((project) => (
                      <ComboboxOption
                        as="li"
                        key={project.id}
                        value={{ ...project, url: `?project=${project.id}` }}
                        className={({ active }) =>
                          cn(
                            "group flex cursor-default select-none items-center rounded-md px-3 py-2",
                            active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                          )
                        }
                      >
                        <FolderIcon
                          className="h-5 w-5 flex-none text-gray-400 group-data-[active]:text-gray-500"
                          aria-hidden="true"
                        />
                        <span className="ml-3 flex-auto truncate">{project.name}</span>
                        <span className="ml-3 hidden flex-none text-gray-400 group-data-[active]:inline">
                          Jump to...
                        </span>
                      </ComboboxOption>
                    ))}
                  </ul>
                </li>
                {query === '' && (
                  <li className="p-2">
                    <h2 className="sr-only">Quick actions</h2>
                    <ul className="text-sm text-gray-700">
                      {quickActions.map((action) => (
                        <ComboboxOption
                          as="li"
                          key={action.shortcut}
                          value={action}
                          className={({ active }) =>
                            cn(
                              "group flex cursor-default select-none items-center rounded-md px-3 py-2",
                              active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                            )
                          }
                        >
                          <action.icon
                            className="h-5 w-5 flex-none text-gray-400 group-data-[active]:text-gray-500"
                            aria-hidden="true"
                          />
                          <span className="ml-3 flex-auto truncate">{action.name}</span>
                          <span className="ml-3 flex-none text-xs font-medium text-gray-400 group-data-[active]:text-gray-500">
                            <kbd className="font-sans">⌘</kbd>
                            <kbd className="font-sans">{action.shortcut}</kbd>
                          </span>
                        </ComboboxOption>
                      ))}
                    </ul>
                  </li>
                )}
              </ComboboxOptions>
            )}

            {!isLoadingProjects && query !== '' && filteredProjects.length === 0 && (
              <div className="px-6 py-14 text-center sm:px-14">
                <FolderIcon className="mx-auto h-6 w-6 text-gray-400" aria-hidden="true" />
                <p className="mt-4 text-sm text-gray-900">
                  No results found for "{query}". Try a different search term.
                </p>
              </div>
            )}
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  )
} 